import { useState } from "react";
import { track } from "@/lib/track";
import { Grid3x3 } from "lucide-react";

import PhotoTourModal from "./PhotoTourModal";
import type { PhotoEntry } from "@/lib/photoAreas";
import { useIsMobile } from "@/hooks/use-mobile";
import Img from "@/components/Img";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

interface PhotoGalleryProps {
  /** Source of truth, ordered, with optional area tag per photo. */
  photos: PhotoEntry[];
  title: string;
}

const PhotoGallery = ({ photos, title }: PhotoGalleryProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [initialMode, setInitialMode] = useState<"tour" | "zoom">("tour");
  const [initialIndex, setInitialIndex] = useState(0);
  // Mobile-only: track the visible slide so the "n / total" chip stays in sync.
  const [carouselIndex, setCarouselIndex] = useState(0);

  const openTour = () => {
    track("gallery_open", { photos: photos.length });
    setInitialMode("tour");
    setInitialIndex(0);
    setOpen(true);
  };

  const openAt = (i: number) => {
    setInitialMode("zoom");
    setInitialIndex(i);
    setOpen(true);
  };

  if (photos.length === 0) return null;

  // Mobile: swipeable carousel through every photo. Tap any slide to open
  // the full lightbox at that index; the "Show all photos" pill still opens
  // the grid view. Matches PropertyCard's mobile-carousel UX so swipe is
  // discoverable everywhere photos appear.
  if (isMobile) {
    return (
      <>
        <div className="relative rounded-card overflow-hidden mb-8">
          <Carousel
            className="w-full"
            opts={{ loop: photos.length > 1 }}
            setApi={(api) => {
              if (!api) return;
              setCarouselIndex(api.selectedScrollSnap());
              api.on("select", () => setCarouselIndex(api.selectedScrollSnap()));
            }}
          >
            <CarouselContent className="ml-0">
              {photos.map((photo, i) => (
                <CarouselItem key={i} className="pl-0 touch-pan-y">
                  <button
                    type="button"
                    onClick={() => openAt(i)}
                    className="block w-full aspect-[4/3] overflow-hidden"
                    aria-label={`Open photo ${i + 1}`}
                  >
                    <Img
                      src={photo.url}
                      transform={{ width: 1200 }}
                      srcSetWidths={[640, 828, 1200]}
                      sizes="100vw"
                      priority={i === 0}
                      alt={photo.alt ?? `${title} photo ${i + 1}`}
                      draggable={false}
                      className="w-full h-full object-cover select-none"
                    />
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {photos.length > 1 && (
            <div className="absolute bottom-4 left-4 z-10 px-2.5 py-1 rounded-full bg-charcoal/70 text-white text-[12px] font-medium backdrop-blur-sm pointer-events-none tabular-nums">
              {carouselIndex + 1} / {photos.length}
            </div>
          )}
          <button
            type="button"
            onClick={openTour}
            className="absolute bottom-4 right-4 z-10 bg-white text-charcoal rounded-full px-3.5 py-1.5 inline-flex items-center gap-2 shadow-elevated font-medium text-[12px] border border-charcoal/10 active:scale-[0.98] transition-all"
          >
            <Grid3x3 className="h-3.5 w-3.5" />
            All photos
            {photos.length > 1 && (
              <span className="text-[11px] text-charcoal/60 tabular-nums">
                {photos.length}
              </span>
            )}
          </button>
        </div>

        <PhotoTourModal
          open={open}
          onOpenChange={setOpen}
          photos={photos}
          title={title}
          initialMode={initialMode}
          initialIndex={initialIndex}
        />
      </>
    );
  }

  // Desktop: 1 large + 4 thumbnails grid. Single-photo listings collapse to
  // one tile.
  const visible = photos.slice(0, 5);
  const hasOnlyOne = visible.length === 1;

  return (
    <>
      <div
        className={`relative grid gap-2 rounded-card overflow-hidden mb-8 ${
          hasOnlyOne
            ? "grid-cols-1"
            : "grid-cols-4 grid-rows-2 h-[460px] lg:h-[520px]"
        }`}
      >
        {visible.map((photo, i) => (
          <button
            key={i}
            onClick={() => openAt(i)}
            className={`relative overflow-hidden group cursor-pointer ${
              i === 0
                ? hasOnlyOne
                  ? "aspect-[16/9]"
                  : "col-span-2 row-span-2 h-full"
                : "h-full"
            }`}
            aria-label={`Open photo ${i + 1}`}
          >
            <Img
              src={photo.url}
              transform={{ width: i === 0 ? 1200 : 600 }}
              srcSetWidths={i === 0 ? [640, 960, 1280, 1600] : [300, 450, 600, 900]}
              sizes={i === 0 ? "(max-width: 1023px) 100vw, 60vw" : "(max-width: 1023px) 50vw, 20vw"}
              priority={i === 0}
              alt={photo.alt ?? `${title} photo ${i + 1}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors" />
          </button>
        ))}

        <button
          type="button"
          onClick={openTour}
          className="absolute bottom-4 right-4 z-10 bg-white text-charcoal rounded-full px-4 py-2 inline-flex items-center gap-2 shadow-elevated font-medium text-[13px] border border-charcoal/10 hover:bg-white/95 active:scale-[0.98] transition-all"
        >
          <Grid3x3 className="h-4 w-4" />
          Show all photos
          {photos.length > 1 && (
            <span className="text-[12px] text-charcoal/60 tabular-nums">
              {photos.length}
            </span>
          )}
        </button>
      </div>

      <PhotoTourModal
        open={open}
        onOpenChange={setOpen}
        photos={photos}
        title={title}
        initialMode={initialMode}
        initialIndex={initialIndex}
      />
    </>
  );
};

export default PhotoGallery;
